import React, { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress } from "@mui/material";
import { Image as ImageIcon } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import apiService from "../services/api";
import {
  RadarPodscopeRankingCard,
  RANKING_COPY,
  RANKING_PARAM_VALUES,
  buildChannelsSocialFooterExplainer,
  formatViewsFooterExplainer,
  formatInteractionsFooterExplainer,
  formatInteractionsTotalFooterExplainer,
  formatChannelsViewsFooterExplainer,
  formatChannelsInteractionsFooterExplainer,
  formatProgramsBrandsFooterExplainer,
  formatTvDigitalProgramsBrandsFooterExplainer,
  TV_DIGITAL_PROGRAMS_BRANDS_SUBHEADER,
  formatMixedDigitalTvBrandsFooterExplainer,
  MIXED_DIGITAL_TV_BRANDS_SUBHEADER,
  formatLivestreamProgramsProgramGroupFooterExplainer,
  isExcludedLivestreamRankingChannelName,
  isExplorerLivestreamRankingParam,
  resolveLivestreamProgramRowImages,
  buildPodcastProgramImageByName,
  type ProgramRow,
  type ChannelRow,
  type RankingParam,
} from "../components/ranking-card";
import {
  RANKING_ASSET_HEIGHT_PX,
  RANKING_ASSET_WIDTH_PX,
} from "../components/ranking-card/rankingAssetDimensions";
import { getLivestreamRankingDateRangeFromSearchParams } from "../utils/livestreamRankingDateRange";
import { PROGRAM_ANALYSIS_ROUTE_TAXONOMY, resolveProgramAnalysisTaxonomyFromSearchParams } from "../constants/programAnalysisTaxonomy";
import { buildChannelsViewsRankingRows, buildChannelsInteractionsRankingRows, countChannelsFromPrograms } from "./ProgramAnalysis/top10Aggregate";
import {
  downloadProgramAssetPng,
  useProgramAssetAutoDownload,
} from "../utils/programAssetPngExport";
import AssetPreviewStage from "../components/asset-preview/AssetPreviewStage";
import { waitAssetPreviewPaint } from "../components/asset-preview/assetPreviewViewport";

const RANKING_MONTHS_ES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function buildRankingPeriodLabel(
  week?: string,
  startDate?: string,
  endDate?: string,
): string {
  if (week) {
    const start = new Date(week + "T12:00:00");
    if (!isNaN(start.getTime())) {
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      const d1 = start.getDate();
      const d2 = end.getDate();
      const month1 = RANKING_MONTHS_ES[start.getMonth()];
      const month2 = RANKING_MONTHS_ES[end.getMonth()];
      if (month1 === month2) {
        return `Semana del ${d1} al ${d2} de ${month1}`;
      }
      return `Semana del ${d1} de ${month1} al ${d2} de ${month2}`;
    }
  }
  if (startDate && endDate) return `${startDate} – ${endDate}`;
  return "Período seleccionado";
}

/**
 * Bare-bones render of the ranking card for PNG export (html2canvas).
 * Supports: views | interactions | channels-social | livestream-programs | livestream-programs-program | programs-brands
 */
const ProgramAnalysisRankingAssetRender: React.FC = () => {
  const [searchParams] = useSearchParams();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const shouldAutoDownload = searchParams.get("download") === "1";

  const ranking = searchParams.get("ranking") ?? "views";
  const week = searchParams.get("week") ?? undefined;
  const podcastTaxonomy = useMemo(
    () => resolveProgramAnalysisTaxonomyFromSearchParams(searchParams),
    [searchParams],
  );
  const podcastId = searchParams.get("podcastId") ?? undefined;
  const startDate = searchParams.get("startDate") ?? undefined;
  const endDate = searchParams.get("endDate") ?? undefined;
  const sortByParam = searchParams.get("sortBy") ?? undefined;
  const sortOrderParam = searchParams.get("sortOrder") ?? "desc";
  const minContentCountParam = searchParams.get("minContentCount");
  const customTitle = searchParams.get("customTitle") ?? undefined;
  const hidePeriodPill = searchParams.get("hidePeriodPill") === "true";
  const worldCupOnly =
    searchParams.get("worldCupOnly") === "true" ||
    ranking.startsWith("world-cup-");
  const validRanking = (
    RANKING_PARAM_VALUES.includes(ranking as RankingParam) ? ranking : "views"
  ) as RankingParam;
  /** Programs-brands asset limited to 100% digital programs on TV channels. */
  const tvDigitalPrograms =
    validRanking === "programs-brands" &&
    searchParams.get("tvDigitalPrograms") === "1";
  /** Programs-brands asset across digital channels and TV digital programs. */
  const mixDigitalTv =
    validRanking === "programs-brands" &&
    !tvDigitalPrograms &&
    searchParams.get("mixDigitalTv") === "1";

  /** `programs` = Explorer `livestream_program` grouping. URL `livestreamGroup=programs` or ranking `livestream-programs-program`. */
  const livestreamGroup =
    validRanking === "livestream-programs-program" ||
    validRanking === "world-cup-livestream-programs" ||
    searchParams.get("livestreamGroup") === "programs"
      ? "programs"
      : "channels";

  /** Explorer category filter: all channel types vs digital-only (default: digital). */
  const livestreamCategory =
    searchParams.get("livestreamCategory") === "all" ? "all" : "digital";

  // Views / interactions: always order by 7d metric (ignore legacy sortBy from URL)
  const sortBy =
    validRanking === "views" || validRanking === "world-cup-views"
      ? "avgViewsDay7"
      : validRanking === "interactions"
        ? "avgInteractionsDay7"
        : validRanking === "interactions-total"
          ? "totalInteractionsDay7"
        : sortByParam ??
          (validRanking === "programs-brands"
            ? "uniquePaidBrandsCount"
            : "avgViewsDay7");
  const sortOrder =
    sortOrderParam === "asc" || sortOrderParam === "desc"
      ? sortOrderParam
      : "desc";
  const minContentCount =
    minContentCountParam != null && minContentCountParam !== ""
      ? parseInt(minContentCountParam, 10)
      : undefined;

  const queryParams = {
    week,
    podcastTaxonomy,
    podcastId: podcastId && podcastId !== "all" ? podcastId : undefined,
    startDate,
    endDate,
    sortBy,
    sortOrder,
    minContentCount,
  };

  const livestreamDateRange = useMemo(
    () =>
      getLivestreamRankingDateRangeFromSearchParams(week, startDate, endDate),
    [week, startDate, endDate],
  );

  const programsQuery = useQuery({
    queryKey: [
      "programs-analysis-ranking-render",
      { ...queryParams, ranking: validRanking, tvDigitalPrograms, mixDigitalTv },
    ],
    queryFn: () =>
      apiService.getProgramsAnalysis(
        week,
        podcastTaxonomy,
        queryParams.podcastId,
        sortBy,
        sortOrder,
        startDate,
        endDate,
        validRanking === "programs-brands",
        minContentCount,
        validRanking === "world-cup-views" || worldCupOnly,
        tvDigitalPrograms,
        mixDigitalTv,
      ),
    enabled:
      validRanking === "views" ||
      validRanking === "channels-views" ||
      validRanking === "interactions" ||
      validRanking === "interactions-total" ||
      validRanking === "channels-interactions" ||
      validRanking === "programs-brands" ||
      validRanking === "world-cup-views" ||
      validRanking === "livestream-programs-program" ||
      validRanking === "world-cup-livestream-programs",
  });

  const channelsQuery = useQuery({
    queryKey: ["channels-social-ranking-render", queryParams],
    queryFn: () =>
      apiService.getChannelsSocialAnalytics(
        week,
        podcastTaxonomy,
        queryParams.podcastId,
        undefined,
        startDate,
        endDate,
      ),
    enabled: validRanking === "channels-social",
  });

  const worldCupLivestreamQuery = useQuery({
    queryKey: [
      "ranking-world-cup-livestream-asset-render",
      livestreamDateRange,
    ],
    queryFn: () =>
      apiService.getExplorerLivestreams(
        livestreamDateRange!.startDate,
        livestreamDateRange!.endDate,
        "all",
        "all",
        {
          worldCupOnly: true,
          ranking: {
            page: 0,
            pageSize: 30,
            mode: "programs",
            sortColumn: "avgViewers",
            sortDirection: "desc",
          },
        },
      ),
    enabled:
      validRanking === "world-cup-livestream-programs" &&
      livestreamDateRange != null,
  });

  const livestreamQuery = useQuery({
    queryKey: [
      "ranking-livestream-asset-render",
      livestreamCategory,
      livestreamGroup,
      livestreamDateRange,
    ],
    queryFn: () =>
      apiService.getExplorerLivestreams(
        livestreamDateRange!.startDate,
        livestreamDateRange!.endDate,
        "all",
        livestreamCategory,
        livestreamGroup === "programs"
          ? {
              ranking: {
                page: 0,
                pageSize: 30,
                mode: "programs",
                sortColumn: "avgViewers",
                sortDirection: "desc",
              },
            }
          : undefined,
      ),
    enabled:
      isExplorerLivestreamRankingParam(validRanking) &&
      livestreamDateRange != null,
  });

  const podcastProgramImageByName = useMemo(
    () =>
      buildPodcastProgramImageByName(
        (programsQuery.data?.programs ?? []) as Array<{
          name: string;
          imageS3Key?: string | null;
          imageUpdatedAt?: string | null;
        }>,
      ),
    [programsQuery.data?.programs],
  );

  const isLoading =
    validRanking === "views" ||
    validRanking === "channels-views" ||
    validRanking === "interactions" ||
    validRanking === "interactions-total" ||
    validRanking === "channels-interactions" ||
    validRanking === "programs-brands" ||
    validRanking === "world-cup-views"
      ? programsQuery.isLoading
      : validRanking === "channels-social"
        ? channelsQuery.isLoading
        : validRanking === "world-cup-livestream-programs"
          ? worldCupLivestreamQuery.isLoading ||
            programsQuery.isLoading
          : isExplorerLivestreamRankingParam(validRanking)
            ? livestreamQuery.isLoading ||
              (validRanking === "livestream-programs-program"
                ? programsQuery.isLoading
                : false)
            : false;

  const activeLivestreamQuery =
    validRanking === "world-cup-livestream-programs"
      ? worldCupLivestreamQuery
      : livestreamQuery;

  // Use backend order and position data (same as programs tab) — do not re-sort
  const programs = useMemo((): ProgramRow[] => {
    if (
      validRanking !== "views" &&
      validRanking !== "interactions" &&
      validRanking !== "interactions-total" &&
      validRanking !== "programs-brands" &&
      validRanking !== "world-cup-views"
    )
      return [];
    const list = (programsQuery.data?.programs ?? []) as any[];
    return list.slice(0, 10).map((p: any, index: number) => ({
      _id: p._id ?? p.programId,
      name: p.name,
      imageS3Key: p.imageS3Key,
      imageUpdatedAt: p.imageUpdatedAt,
      updatedAt: p.updatedAt,
      podcast: p.podcast ?? { _id: "", title: "" },
      avgViews: p.avgViews ?? 0,
      avgViewsDay7:
        typeof p.avgViewsDay7 === "number" ? p.avgViewsDay7 : undefined,
      avgInteractions: p.avgInteractions,
      avgInteractionsDay7:
        typeof p.avgInteractionsDay7 === "number"
          ? p.avgInteractionsDay7
          : undefined,
      totalInteractionsDay7:
        typeof p.totalInteractionsDay7 === "number"
          ? p.totalInteractionsDay7
          : undefined,
      uniquePaidBrandsCount: p.uniquePaidBrandsCount,
      rankingPosition: p.rankingPosition ?? index + 1,
      positionLastWeek: p.positionLastWeek ?? null,
    }));
  }, [programsQuery.data, validRanking]);

  const channelsSocial = useMemo((): ChannelRow[] => {
    if (validRanking !== "channels-social") return [];
    return (channelsQuery.data?.channels ?? []).map((c: any) => ({
      _id: c._id,
      name: c.name,
      imageS3Key: c.imageS3Key,
      imageUrl: c.imageUrl,
      imageUpdatedAt: c.imageUpdatedAt,
      updatedAt: c.updatedAt,
      totalViews: c.totalViews,
      contentCount: c.contentCount,
    }));
  }, [channelsQuery.data, validRanking]);

  const channelsLivestream = useMemo((): ChannelRow[] => {
    if (
      !isExplorerLivestreamRankingParam(validRanking) &&
      validRanking !== "world-cup-livestream-programs"
    ) {
      return [];
    }
    const data = activeLivestreamQuery.data;
    if (!data) return [];

    if (livestreamGroup === "programs") {
      const imageByChannelId = new Map(
        (data.channels ?? []).map((c: { _id: string; imageS3Key?: string }) => [
          String(c._id),
          c.imageS3Key,
        ] as const),
      );
      const programRows = data.ranking?.programRows ?? [];
      return programRows
        .filter(
          (row) =>
            !isExcludedLivestreamRankingChannelName(row.sampleChannelName),
        )
        .slice(0, 10)
        .map((row) => {
          const images = resolveLivestreamProgramRowImages(
            row,
            podcastProgramImageByName,
            imageByChannelId,
          );
          // Main thumb: program cover, else channel image as fallback.
          const mainImage =
            images.programImage ??
            (images.channelImageS3Key
              ? { imageS3Key: images.channelImageS3Key }
              : undefined);
          return {
            _id: row.key,
            name: row.programName,
            imageS3Key: mainImage?.imageS3Key,
            imageUpdatedAt: images.programImage?.imageUpdatedAt,
            // Channel badge even when it matches the program cover.
            channelImageS3Key: images.channelImageS3Key,
            avgLiveViewers: row.metrics?.avgViewers ?? 0,
            peakLiveViewers: row.metrics?.maxPeakViewers ?? 0,
            avgUniqueChatUsers: row.metrics?.avgUniqueChatUsers ?? 0,
            livestreamCount: row.episodeCount,
          };
        });
    }

    const list = (data.channels ?? []).filter(
      (c) => !isExcludedLivestreamRankingChannelName(c.channelName),
    );
    return [...list]
      .sort(
        (a, b) => (b.metrics?.avgViewers ?? 0) - (a.metrics?.avgViewers ?? 0),
      )
      .slice(0, 10)
      .map((c) => ({
        _id: String(c._id ?? c.channelId ?? c.channelName),
        name: c.channelName,
        imageS3Key: c.imageS3Key,
        avgLiveViewers: c.metrics?.avgViewers ?? 0,
        peakLiveViewers: c.metrics?.maxPeakViewers ?? 0,
        avgUniqueChatUsers: c.metrics?.avgUniqueChatUsers ?? 0,
        livestreamCount: c.episodeCount ?? c.livestreams?.length ?? 0,
      }));
  }, [activeLivestreamQuery.data, validRanking, livestreamGroup, podcastProgramImageByName]);

  const channelsViews = useMemo((): ChannelRow[] => {
    if (validRanking !== "channels-views") return [];
    return buildChannelsViewsRankingRows(
      (programsQuery.data?.programs ?? []) as Parameters<
        typeof buildChannelsViewsRankingRows
      >[0],
    );
  }, [programsQuery.data, validRanking]);

  const channelsInteractions = useMemo((): ChannelRow[] => {
    if (validRanking !== "channels-interactions") return [];
    return buildChannelsInteractionsRankingRows(
      (programsQuery.data?.programs ?? []) as Parameters<
        typeof buildChannelsInteractionsRankingRows
      >[0],
    );
  }, [programsQuery.data, validRanking]);

  const channelsForCard =
    validRanking === "channels-views"
      ? channelsViews
      : validRanking === "channels-interactions"
        ? channelsInteractions
      : validRanking === "channels-social"
        ? channelsSocial
        : isExplorerLivestreamRankingParam(validRanking) ||
            validRanking === "world-cup-livestream-programs"
          ? channelsLivestream
          : undefined;

  const channelsMetric =
    isExplorerLivestreamRankingParam(validRanking) ||
    validRanking === "world-cup-livestream-programs"
      ? "livestream-avg"
      : "social-views";

  const rankingPeriodLabel = useMemo(
    () => buildRankingPeriodLabel(week, startDate, endDate),
    [week, startDate, endDate],
  );

  const copyKey =
    validRanking === "views"
      ? "views"
      : validRanking === "channels-views"
        ? "channelsViews"
      : validRanking === "world-cup-views"
        ? "worldCupViews"
      : validRanking === "interactions"
        ? "interactions"
        : validRanking === "interactions-total"
          ? "interactionsTotal"
        : validRanking === "channels-interactions"
          ? "channelsInteractions"
        : validRanking === "channels-social"
          ? "channelsSocial"
          : validRanking === "programs-brands"
            ? "programsBrands"
            : validRanking === "world-cup-livestream-programs"
              ? "worldCupLivestreamPrograms"
            : "livestreamPrograms";
  const copy =
    validRanking === "livestream-programs-program" ||
    validRanking === "world-cup-livestream-programs" ||
    (validRanking === "livestream-programs" && livestreamGroup === "programs")
      ? validRanking === "world-cup-livestream-programs"
        ? RANKING_COPY.worldCupLivestreamPrograms
        : RANKING_COPY.livestreamProgramsProgramGroup
      : RANKING_COPY[copyKey];

  const isLivestreamProgramGroupRanking =
    validRanking === "livestream-programs-program" ||
    (validRanking === "livestream-programs" && livestreamGroup === "programs");
  const livestreamProgramGroupCount =
    activeLivestreamQuery.data?.ranking?.total ?? 0;

  const hasData = programs.length > 0 || (channelsForCard?.length ?? 0) > 0;
  const isReady = hasData && !isLoading;
  const pngFilename = `ranking-${validRanking}${tvDigitalPrograms ? "-tv-digital" : mixDigitalTv ? "-digital-tv" : ""}-${rankingPeriodLabel.replace(/\s+/g, "-").replace(/[^a-zA-Z0-9-]/g, "")}.png`;

  useProgramAssetAutoDownload(
    cardRef,
    shouldAutoDownload,
    isReady,
    pngFilename,
    { width: RANKING_ASSET_WIDTH_PX, height: RANKING_ASSET_HEIGHT_PX },
  );

  const handleDownloadPng = async () => {
    flushSync(() => setIsDownloading(true));
    await waitAssetPreviewPaint();
    try {
      await downloadProgramAssetPng(cardRef.current, pngFilename, {
        width: RANKING_ASSET_WIDTH_PX,
        height: RANKING_ASSET_HEIGHT_PX,
      });
    } catch (err) {
      console.error("Ranking asset PNG download failed:", err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: 1080,
          height: 1350,
          bgcolor: "#1a1a1a",
        }}
      >
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  if (!hasData) return null;

  return (
    <AssetPreviewStage
      widthPx={RANKING_ASSET_WIDTH_PX}
      heightPx={RANKING_ASSET_HEIGHT_PX}
      disabled={shouldAutoDownload || isDownloading}
      toolbarExtra={
        process.env.NODE_ENV === "development" ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isDownloading ? <CircularProgress size={16} /> : <ImageIcon />
            }
            onClick={handleDownloadPng}
            disabled={isDownloading}
            sx={{
              position: "fixed",
              top: 8,
              right: 8,
              zIndex: 12,
              bgcolor: "background.paper",
            }}
          >
            {isDownloading ? "Generando…" : "Download PNG"}
          </Button>
        ) : null
      }
    >
      <Box ref={cardRef}>
        <RadarPodscopeRankingCard
      programs={
        validRanking === "views" ||
        validRanking === "interactions" ||
        validRanking === "interactions-total" ||
        validRanking === "programs-brands" ||
        validRanking === "world-cup-views"
          ? programs
          : undefined
      }
      channels={channelsForCard}
      rankingPeriodLabel={rankingPeriodLabel}
      subheader={
        tvDigitalPrograms
          ? TV_DIGITAL_PROGRAMS_BRANDS_SUBHEADER
          : mixDigitalTv
            ? MIXED_DIGITAL_TV_BRANDS_SUBHEADER
            : (customTitle ?? copy.subheader)
      }
      hidePeriodPill={hidePeriodPill}
      footerExplainer={
        validRanking === "channels-social"
          ? buildChannelsSocialFooterExplainer(week, startDate, endDate)
          : validRanking === "channels-views"
            ? formatChannelsViewsFooterExplainer(
                countChannelsFromPrograms(
                  (programsQuery.data?.programs ?? []) as Parameters<
                    typeof countChannelsFromPrograms
                  >[0],
                ),
              )
          : validRanking === "channels-interactions"
            ? formatChannelsInteractionsFooterExplainer(
                countChannelsFromPrograms(
                  (programsQuery.data?.programs ?? []) as Parameters<
                    typeof countChannelsFromPrograms
                  >[0],
                ),
              )
          : validRanking === "interactions"
            ? formatInteractionsFooterExplainer(
                programsQuery.data?.activeProgramCount ??
                  (programsQuery.data?.programs ?? []).length,
              )
            : validRanking === "interactions-total"
              ? formatInteractionsTotalFooterExplainer(
                  programsQuery.data?.activeProgramCount ??
                    (programsQuery.data?.programs ?? []).length,
                )
            : validRanking === "views"
              ? formatViewsFooterExplainer(
                  programsQuery.data?.activeProgramCount ??
                    (programsQuery.data?.programs ?? []).length,
                )
            : validRanking === "programs-brands"
              ? tvDigitalPrograms
                ? formatTvDigitalProgramsBrandsFooterExplainer(
                    programsQuery.data?.totalUniqueBrands ?? 0,
                  )
                : mixDigitalTv
                  ? formatMixedDigitalTvBrandsFooterExplainer(
                      programsQuery.data?.totalUniqueBrands ?? 0,
                    )
                  : formatProgramsBrandsFooterExplainer(
                      programsQuery.data?.totalUniqueBrands ?? 0,
                    )
              : isLivestreamProgramGroupRanking
                ? formatLivestreamProgramsProgramGroupFooterExplainer(
                    livestreamProgramGroupCount,
                  )
                : copy.footerExplainer
      }
      programRankingMetric={
        validRanking === "views" || validRanking === "world-cup-views"
          ? "views"
          : validRanking === "interactions"
            ? "interactions"
            : validRanking === "interactions-total"
              ? "interactions-total"
            : validRanking === "programs-brands"
              ? "programs-brands"
              : undefined
      }
      channelsMetric={channelsForCard ? channelsMetric : undefined}
      rankingParam={validRanking}
      variant="screenshot"
      livestreamTableNameColumnHeader={
        validRanking === "livestream-programs-program" ||
        validRanking === "world-cup-livestream-programs" ||
        (validRanking === "livestream-programs" &&
          livestreamGroup === "programs")
          ? "Programa"
          : "Canal"
      }
        />
      </Box>
    </AssetPreviewStage>
  );
};

export default ProgramAnalysisRankingAssetRender;
